import React from 'react';
import { motion } from 'framer-motion';
import { trackCTA } from '../utils/analytics';
import { useLanguage } from '../context/LanguageContext';
import { content } from '../i18n/content';

const PROJECTS_BASE = [
  {
    name: 'Grano Santo',
    image: 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=800&h=600&fit=crop',
    link: 'https://landing-cafe-amber.vercel.app/'
  },
  {
    name: 'FundArte Cojutepeque',
    image: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=800&h=600&fit=crop',
    link: 'https://fundartecojutepeque.org/'
  },
  {
    name: 'Clínica Dental Premium',
    image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=800&h=600&fit=crop',
    link: 'https://clinica-dental-premium-ruddy.vercel.app/'
  },
  {
    name: 'Bufete',
    image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&h=600&fit=crop',
    link: 'https://bufete-template.vercel.app/'
  },
  {
    name: 'Hotel Boutique',
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=600&fit=crop',
    link: 'https://hotel-landing-gules.vercel.app/'
  },
  {
    name: 'Gimnasio',
    image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&h=600&fit=crop',
    link: 'https://gym-landing-amber.vercel.app/'
  }
];

const Portfolio = () => {
  const { language } = useLanguage();
  const t = content[language].portfolio;
  const projects = PROJECTS_BASE.map((p) => ({ ...p, description: t.descriptions[p.name] }));

  return (
    <section id="portfolio" className="py-24 relative bg-primary premium-bg-1">
      <div className="container mx-auto px-4 md:px-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-12 md:mb-16 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
            <span className="text-secondary text-sm">✦</span>
            <span className="text-white/70 text-sm font-medium tracking-widest uppercase">{t.badge}</span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-white leading-tight mb-4">
            {t.titleLine1} <span className="text-secondary">{t.titleLine2}</span>
          </h2>
          <p className="text-white/60 text-base md:text-lg font-light">
            {t.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {projects.map((project, index) => (
            <motion.a
              key={index}
              href={project.link}
              target={project.link !== '#' ? '_blank' : '_self'}
              rel={project.link !== '#' ? 'noopener noreferrer' : ''}
              onClick={() => trackCTA('portfolio', `Ver proyecto: ${project.name}`)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
              className="group relative w-full aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer shadow-xl border border-white/10"
            >
              <img
                src={project.image}
                alt={project.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/40 to-transparent group-hover:from-primary/95 group-hover:via-primary/60 transition-colors duration-300"></div>

              <div className="absolute bottom-0 left-0 w-full p-5 md:p-6">
                <h3 className="font-display text-lg md:text-xl font-bold text-white mb-1 drop-shadow-lg">
                  {project.name}
                </h3>
                <p className="text-light-gray/80 text-sm font-light mb-3 line-clamp-2">
                  {project.description}
                </p>
                <div className="inline-flex items-center gap-2 text-secondary text-sm font-medium opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                  {t.viewProject}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;

